// Lab Partner Portal — root component
//
// Mirrors MedicalDashboard.js exactly: same animated brand block, scoped
// Outfit font, gradient teal-green sidebar, rich topbar title with icon
// tile + eyebrow + accent line, dynamic user card pinned to the bottom
// of the sidebar, "Back to Website" footer button. The only differences
// from the medical portal are the wording ("Medical Portal" → "Lab
// Partner Portal") and the queries-badge count.
//
// Page bodies are reused from ./medical/* since both portals share the
// same internal screens (reports, queries, requisitions, analytics) per
// the design spec — wrapping them in a different sidebar / topbar is
// what makes one a Medical portal and the other a Lab Partner portal.
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PortalLayout from '../../layouts/PortalLayout';
import markSrc from '../../assets/img/trustlab-mark.png';
import { SidebarNavItem, BiIcon } from '../../layouts/SidebarNav';
import { ROUTES } from '../../constants/routes';
import { useAuth } from '../../context/AuthContext';
import { useListPortalComplaintsQuery } from '../../store/queriesApi';

// Sidebar icons are Bootstrap Icons, named as strings on the menu entries
// below — the same names and tints the medical portal uses for the ids the
// two share, so the rails read as one product.

import LabOverview from './lab/LabOverview';
import EdosTests from './lab/EdosTests';
import EdosDepartments from './lab/EdosDepartments';
import EdosSpeciality from './lab/EdosSpeciality';
import EdosTrfForms from './lab/EdosTrfForms';
import PatientReports from './medical/PatientReports';
import ReportQueries from './medical/ReportQueries';
import RaiseQuery from './medical/RaiseQuery';
import RaiseTicket from './medical/RaiseTicket';
import Analytics from './medical/Analytics';
import PartnerFeedbackPage from './feedback/PartnerFeedbackPage';
import PartnerFeedbackModal from './feedback/PartnerFeedbackModal';
import ChangePasswordModal from '../../components/common/ChangePasswordModal';

// The same scoped-font system the medical portal uses. Both portals
// share the body class so the @font-face + global rule injected by
// whichever portal mounts first applies to the other too — no double
// fetch, no double <style> tag. Kept the original attribute / class
// names so a fresh load via either portal still no-ops correctly on
// the second mount.
const FONT_TAG = 'data-medical-portal-font';
const RULE_TAG = 'data-medical-portal-rule';
const BODY_CLASS = 'medical-portal-active';

const loadPortalFont = () => {
  if (!document.querySelector(`style[${FONT_TAG}]`)) {
    fetch('https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600;700&display=swap')
      .then((r) => (r.ok ? r.text() : Promise.reject()))
      .then((css) => {
        // Two families, two private names. Both re-declarations have to
        // happen in this one stylesheet: Google returns them interleaved in
        // a single response, so splitting the fetch would double the
        // round-trips for no gain.
        const scoped = css
          .replace(/font-family:\s*'IBM Plex Sans'/g, "font-family: 'MedicalPortalSans'")
          .replace(/font-family:\s*'IBM Plex Mono'/g, "font-family: 'MedicalPortalMono'");
        const style = document.createElement('style');
        style.setAttribute(FONT_TAG, '');
        style.textContent = scoped;
        document.head.appendChild(style);
      })
      .catch(() => { /* font fetch failed; portal falls back to sans-serif */ });
  }

  if (!document.querySelector(`style[${RULE_TAG}]`)) {
    const rule = document.createElement('style');
    rule.setAttribute(RULE_TAG, '');
    // EVERY SELECTOR IS WRAPPED IN `:where()`, WHICH IS THE POINT.
    //
    // `!important` is unavoidable here — it is the only thing that beats
    // MUI's own component font-family on the portalled surfaces. But an
    // `!important` on a selector like `body.x *` also beats every sx prop
    // in the portal, which silently flattened all 179 `fontFamily: MONO`
    // sites to sans. `:where()` contributes zero specificity, so this rule
    // still wins against MUI (which sets no `!important`) while LOSING to
    // the `!important` baked into MONO in medical/shared.js. That one-sided
    // outcome is what keeps table headers, IDs and figures monospaced.
    rule.textContent = `
      :where(body.${BODY_CLASS}),
      :where(body.${BODY_CLASS}) *,
      :where(body.${BODY_CLASS} .MuiPopover-root),
      :where(body.${BODY_CLASS} .MuiModal-root),
      :where(body.${BODY_CLASS} .MuiDrawer-root),
      :where(body.${BODY_CLASS} .MuiTooltip-popper),
      :where(body.${BODY_CLASS} .MuiMenu-root),
      :where(body.${BODY_CLASS} .MuiPopover-root) *,
      :where(body.${BODY_CLASS} .MuiModal-root) *,
      :where(body.${BODY_CLASS} .MuiDrawer-root) *,
      :where(body.${BODY_CLASS} .MuiTooltip-popper) *,
      :where(body.${BODY_CLASS} .MuiMenu-root) * {
        font-family: 'MedicalPortalSans', system-ui, sans-serif !important;
      }

      /* NO RUBBER-BAND. Dragging past the top or bottom of the portal used
         to bounce the whole page, which on a dense register reads as the
         table coming loose.

         Two rules, deliberately separate. The first is the one that does
         the work: overscroll-behavior set on BODY propagates to the
         viewport when the root element leaves its own value at 'auto',
         which is the case here. The second says the same thing on the root
         for engines that want it there - split out because :has() is the
         newer feature of the two, and an unsupported selector invalidates
         only its own rule, so the body rule survives either way.

         Both are scoped to the portal's body class, which the dashboard
         removes on unmount, so the rest of the site scrolls as it always
         has. */
      body.${BODY_CLASS} { overscroll-behavior: none; }
      html:has(> body.${BODY_CLASS}) { overscroll-behavior: none; }

      /* And a scroller inside the portal — a long table, the sidebar —
         keeps its overscroll to itself instead of handing it to the page
         once it hits its own end. */
      body.${BODY_CLASS} [class*='MuiDrawer-paper'],
      body.${BODY_CLASS} [class*='MuiDialog-paper'],
      body.${BODY_CLASS} [class*='MuiDialogContent-root'] {
        overscroll-behavior: contain;
      }

      /* LOADING PLACEHOLDERS.
         MUI's "wave" variant sweeps a white gradient across each bar. With
         a dozen bars on screen that is a dozen highlights travelling at
         once, which draws the eye to the waiting rather than to the page.
         The bars are now AIMS's own --rule-2, pulsing slowly in place:
         present enough to say "this is coming", quiet enough to ignore. */
      body.${BODY_CLASS} .MuiSkeleton-root {
        background-color: #EDF1F4;
      }
      /* Belt and braces - the sweep is drawn by a pseudo-element, so a
         stray animation="wave" anywhere still renders as a calm pulse. */
      body.${BODY_CLASS} .MuiSkeleton-wave::after {
        display: none;
      }
      body.${BODY_CLASS} .MuiSkeleton-wave {
        animation: mp-skeleton-pulse 1.6s ease-in-out .5s infinite;
      }
      @keyframes mp-skeleton-pulse {
        0%, 100% { opacity: 1; }
        50%      { opacity: .45; }
      }
      /* Someone who has asked their system for less motion gets none. */
      @media (prefers-reduced-motion: reduce) {
        body.${BODY_CLASS} .MuiSkeleton-root {
          animation: none !important;
          opacity: .75;
        }
      }
    `;
    document.head.appendChild(rule);
  }
};

// Today's date — rendered in the topbar as "Fri, 17 Apr, 2026"
const TODAY_LABEL = (() => {
  const d = new Date();
  const wk = d.toLocaleDateString('en-GB', { weekday: 'short' });
  const dy = d.toLocaleDateString('en-GB', { day: '2-digit' });
  const mo = d.toLocaleDateString('en-GB', { month: 'short' });
  const yr = d.getFullYear();
  return `${wk}, ${dy} ${mo}, ${yr}`;
})();

// Sidebar menu — outline icons that match the medical portal so the
// two surfaces feel like one product. The "Report Queries" badge is
// filled in dynamically inside the component from the live RCA list
// — anything not resolved/closed is "active" and worth alerting on.
// The static menu is just the layout skeleton.
// MENU split into two halves so the eDOS group can sit between
// Dashboard and the rest of the items in the sidebar.
//   TOP_MENU    : Dashboard
//   <eDOS group rendered inline here>
//   BOTTOM_MENU : Patient Reports → Test Requisition → Report Queries
//                 → Raise Query → TrustLab Feedback → Reports & Analytics
// Test Requisition is no longer hidden — surfaced under Patient Reports.
const TOP_MENU = [
  { id: 'dashboard',   icon: 'speedometer2',              tint: '#FF7A45', label: 'Dashboard' },
];
const BOTTOM_MENU = [
  { id: 'reports',     icon: 'file-earmark-medical-fill', tint: '#5FD3F3', label: 'Patient Reports' },
  { id: 'requisition', icon: 'clipboard-pulse',           tint: '#4FD1A5', label: 'Test Requisition' },
  { id: 'queries',     icon: 'exclamation-triangle-fill', tint: '#FF9E4A', label: 'Report Queries' },
  { id: 'new-query',   icon: 'file-earmark-plus-fill',    tint: '#FFC81F', label: 'Raise Query' },
  // Internal CRM ticket (stock / equipment / billing / IT / HR …).
  // Different from Raise Query — that's report-level QA queries; this
  // is for branch-operations issues.
  { id: 'new-ticket',  icon: 'ticket-detailed-fill',      tint: '#B39DFF', label: 'Raise Ticket' },
  // Voice of Partner — feedback submission for the active cycle. The
  // auto-popup modal also fires from this dashboard, so the menu
  // entry is the lab partner's manual route back if they dismissed it.
  { id: 'feedback',    icon: 'megaphone-fill',            tint: '#FF8A80', label: 'TrustLab Feedback' },
  { id: 'analytics',   icon: 'bar-chart-line-fill',       tint: '#2EB1FF', label: 'Reports & Analytics' },
];
// Combined menu used for activeMenu / topbar lookups (order doesn't
// matter for find()).
const MENU = [...TOP_MENU, ...BOTTOM_MENU];

// view_status_label → "active" classification for the queries badge.
// Anything not in this set (resolved / closed / unknown) is excluded,
// so the badge tracks queries that still need attention.
// OPM statuses that still need attention: open, assigned, in_progress.
const isActiveQuery = (rec) => rec?.active === true;

const TITLES = {
  dashboard:   'Dashboard',
  reports:     'Patient Reports',
  requisition: 'Test Requisition Form',
  queries:     'Report Queries',
  'new-query': 'Raise a Query',
  'new-ticket':'Raise a Ticket',
  analytics:   'Reports & Analytics',
  feedback:    'TrustLab Feedback',
  'edos-tests':       'eDOS · Tests',
  'edos-departments': 'eDOS · Departments',
  'edos-speciality':  'eDOS · Speciality',
};

// eDOS sidebar group — parent + three searchable children. Defined
// outside the component so it doesn't rebuild on every render.
const EDOS_GROUP = [
  { id: 'edos-tests',       icon: 'droplet-fill',     tint: '#4FD1A5', label: 'Tests' },
  { id: 'edos-departments', icon: 'diagram-3-fill',   tint: '#B39DFF', label: 'Departments' },
  { id: 'edos-speciality',  icon: 'heart-pulse-fill', tint: '#FF8A80', label: 'Speciality' },
];


const LabDashboard = () => {
  const [active, setActive] = useState('dashboard');
  // eDOS sidebar group expand state. Auto-opens whenever one of the
  // three eDOS sub-routes is active (e.g. on a deep link or back-nav)
  // so the user always sees the highlighted child without having to
  // re-click the parent.
  const [edosOpen, setEdosOpen] = useState(false);
  useEffect(() => {
    if (active.startsWith('edos-')) setEdosOpen(true);
  }, [active]);
  // Holds the visit object passed up from ReportDetailModal → Overview's
  // onNavigate('new-complaint', { visit }). RaiseQuery reads this to
  // pre-select the patient. Cleared whenever navigation lands on
  // anything other than 'new-query' so a stale visit doesn't leak.
  const [prefillVisit, setPrefillVisit] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const userName = user?.lab_name || user?.name || 'Guest';

  // Live "Report Queries" badge — counts queries that still need
  // attention (received / investigating). RTK Query auto-refreshes
  // this whenever a new query is submitted (createRcaInvestigation
  // invalidates the 'Rca' tag), so the badge stays in sync without
  // any manual refetch here.
  // Read from Trust OPM, the same list the Report Queries page shows.
  const { data: rcaListData } = useListPortalComplaintsQuery(
    { clientId: user?.client_id, per_page: 5 },
    { skip: !user?.client_id },
  );
  const activeQueriesCount = rcaListData?.counts?.active ?? (rcaListData?.records || []).filter(isActiveQuery).length;

  // On mount: register the scoped font + global rule once (no-op if the
  // medical portal already injected them in this session), and tag the
  // body so the rule kicks in. On unmount: drop the tag.
  useEffect(() => {
    loadPortalFont();
    document.body.classList.add(BODY_CLASS);
    return () => {
      document.body.classList.remove(BODY_CLASS);
    };
  }, []);

  // Footer slot — "Back to Website" instead of the default Sign Out so
  // lab partners can hop back to the marketing site without losing
  // their session. Same UX choice as the medical portal.
  const backToSite = (
    <Button
      fullWidth
      onClick={() => navigate(ROUTES.HOME)}
      startIcon={<ArrowBackIcon />}
      sx={{
        color: 'rgba(255,255,255,0.75)',
        bgcolor: 'rgba(16,21,28,.10)',
        border: '1px solid rgba(16,21,28,.10)',
        borderRadius: 2,
        fontSize: 13,
        fontWeight: 700,
        textTransform: 'none',
        letterSpacing: '0.2px',
        transition: 'all 0.2s',
        '&:hover': {
          bgcolor: '#0F766E',
          borderColor: '#0F766E',
          color: '#fff',
        },
      }}
    >
      Back to Website
    </Button>
  );

  // Overview uses short keys that differ slightly from menu ids.
  // `params` is optional and currently carries `{ visit }` from the
  // "Raise Query" button in ReportDetailModal — we stash it so
  // RaiseQuery can pre-select the patient on mount.
  const go = (key, params = {}) => {
    const map = {
      'reports': 'reports',
      'new-complaint': 'new-query',
    };
    const next = map[key] || key;
    setPrefillVisit(next === 'new-query' ? (params.visit || null) : null);
    setActive(next);
  };

  // Right-side top-bar action — changes per page
  const topbarAction = useMemo(() => {
    switch (active) {
      case 'queries':
        return (
          <Button
            variant="contained"
            size="small"
            onClick={() => go('new-query')}
            sx={{ bgcolor: '#0F766E', color: '#FFFFFF', '&:hover': { bgcolor: '#0C5F58' } }}
          >
            + New Query
          </Button>
        );
      case 'new-query':
        return (
          <Button variant="outlined" size="small" onClick={() => go('queries')}>
            ← Back
          </Button>
        );
      case 'new-ticket':
        return (
          <Button variant="outlined" size="small" onClick={() => go('dashboard')}>
            ← Back
          </Button>
        );
      case 'requisition':
        return (
          <Button variant="outlined" size="small">📋 My Requisitions</Button>
        );
      case 'reports':
        return (
          <Button
            variant="contained"
            size="small"
            sx={{
              bgcolor: '#0F766E',
              color: '#10151C',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: '#0C5F58' },
            }}
          >
            ⬇ Export All
          </Button>
        );
      case 'analytics':
        return (
          <Button variant="outlined" size="small">⬇ Export PDF</Button>
        );
      default:
        return null;
    }
  }, [active]);

  // Page body dispatcher — reuses medical/* components since the
  // internal screens are identical across portals.
  const body = (() => {
    switch (active) {
      case 'dashboard':
        return <LabOverview onNavigate={go} />;
      case 'reports':
        return <PatientReports onNavigate={go} />;
      case 'requisition':
        return <EdosTrfForms />;
      case 'queries':
        return <ReportQueries onOpenComplaint={() => {}} onNavigate={go} />;
      case 'new-query':
        return (
          <RaiseQuery
            prefillVisit={prefillVisit}
            onBack={() => { setPrefillVisit(null); setActive('queries'); }}
          />
        );
      case 'new-ticket':
        return <RaiseTicket onBack={() => setActive('dashboard')} />;
      case 'analytics':
        return <Analytics />;
      case 'feedback':
        return <PartnerFeedbackPage />;
      case 'edos-tests':
        return <EdosTests />;
      case 'edos-departments':
        return <EdosDepartments />;
      case 'edos-speciality':
        return <EdosSpeciality />;
      default:
        return null;
    }
  })();

  // Rich topbar title — icon tile matching the active route, big title
  // with a teal accent bar, and a "Lab Partner Portal" eyebrow. Fades
  // in on every route change so menu switches feel responsive.
  // Look up the active menu entry — first the top-level MENU, then the
  // eDOS sub-group, so the topbar icon + title still resolve when the
  // user is inside a Search-By view.
  const activeMenu =
    MENU.find((m) => m.id === active) ||
    EDOS_GROUP.find((m) => m.id === active);
  // Brand teal, not the rail tint — the rail colours are far too light to
  // read on the white topbar.
  const activeIcon = activeMenu?.icon
    ? <BiIcon name={activeMenu.icon} size={18} color="#0F766E" />
    : null;

  const labTopbarTitle = (
    <Stack
      key={active}
      direction="row"
      spacing={1.5}
      alignItems="center"
      sx={{
        minWidth: 0,
        animation: 'lp-topbar-fade 0.3s ease-out',
        '@keyframes lp-topbar-fade': {
          from: { opacity: 0, transform: 'translateX(-4px)' },
          to:   { opacity: 1, transform: 'translateX(0)' },
        },
      }}
    >
      <Box
        sx={{
          width: 38, height: 38,
          borderRadius: '10px',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #E6F2F1 0%, #D4EFEB 100%)',
          border: '1px solid #A8CCC8',
          boxShadow: '0 2px 6px rgba(16,21,28,.10)',
        }}
      >
        {activeIcon}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            color: '#0F766E',
            textTransform: 'uppercase',
            letterSpacing: '0.16em',
            lineHeight: 1,
          }}
        >
          Lab Partner Portal
        </Typography>
        <Typography
          sx={{
            fontSize: { xs: 16, md: 20 },
            fontWeight: 700,
            color: '#10151C',
            letterSpacing: '-0.2px',
            lineHeight: 1.2,
            mt: 0.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              left: -10,
              top: '15%',
              bottom: '15%',
              width: 3,
              borderRadius: 2,
              background: 'linear-gradient(180deg, #0F766E 0%, #0C5F58 100%)',
              display: { xs: 'none', md: 'block' },
            },
          }}
        >
          {TITLES[active] || 'Overview'}
        </Typography>
      </Box>
    </Stack>
  );

  return (
    <>
    <PortalLayout
      brand="TrustLab"
      brandSub="Lab Partner Portal"
      // Same brand row as the medical portal — the mark, then who is
      // signed in. See MedicalDashboard.js.
      brandSlot={(
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{ minWidth: 0 }}>
          <Box
            component="img"
            src={markSrc}
            alt="TrustLab Diagnostics"
            sx={{ width: 38, height: 38, objectFit: 'contain', flexShrink: 0, display: 'block' }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: 13.5,
                fontWeight: 700,
                color: '#fff',
                lineHeight: 1.3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {userName}
            </Typography>
            <Typography
              sx={{
                fontSize: 9,
                fontWeight: 600,
                letterSpacing: '.155em',
                textTransform: 'uppercase',
                color: 'rgba(255,255,255,0.45)',
                mt: '2px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Lab Partner Portal
            </Typography>
          </Box>
        </Stack>
      )}
      footerSlot={backToSite}
      accent="#BFEDE7"
      // Same rail as the medical portal — see MedicalDashboard.js.
      sidebarBg={`
        radial-gradient(120% 50% at 0% 100%, rgba(43,158,138,.26) 0%, rgba(43,158,138,0) 62%),
        linear-gradient(168deg, #08322B 0%, #0B4239 34%, #0E4F45 70%, #136055 100%)
      `}
      topbarTitle={TITLES[active] || 'Lab Partner Portal'}
      topbarTitleSlot={labTopbarTitle}
      topbarRight={
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography
            sx={{
              fontSize: 12,
              color: '#4A5763',
              whiteSpace: 'nowrap',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {TODAY_LABEL}
          </Typography>
          {topbarAction}
        </Stack>
      }
      sidebar={
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
          {/* Renders a single menu entry — extracted as a closure so
              the same logic is shared between the Dashboard slot and
              the rest of the menu without duplicating the icon
              cloning / badge / active-styling boilerplate. */}
          {(() => {
            const renderItem = (m) => {
              const isActive = active === m.id;
              const badge = m.id === 'queries'
                ? (activeQueriesCount > 0 ? activeQueriesCount : undefined)
                : m.badge;
              return (
                <SidebarNavItem
                  key={m.id}
                  {...m}
                  badge={badge}
                  iconColor={m.tint}
                  active={isActive}
                  onClick={() => go(m.id)}
                />
              );
            };
            return TOP_MENU.map(renderItem);
          })()}

          {/* eDOS — collapsible group. Clicking the parent toggles
              expand state; the children render under a "Search By"
              label so the visual grouping matches the eDOS HTML
              mock's nav. The chevron rotates 180° when open and the
              children are highlighted in teal when active. */}
          {(() => {
            const edosActive = active.startsWith('edos-');
            return (
              <>
                <Box
                  onClick={() => setEdosOpen((o) => !o)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.25,
                    py: 0.75,
                    minHeight: 44,
                    borderRadius: '10px',
                    border: `1px solid ${edosActive ? 'rgba(46,177,255,.35)' : 'transparent'}`,
                    cursor: 'pointer',
                    color: edosActive ? '#fff' : '#E2EDE8',
                    bgcolor: edosActive ? 'rgba(46,177,255,.16)' : 'transparent',
                    fontSize: 11,
                    fontWeight: 600,
                    letterSpacing: '0.9px',
                    textTransform: 'uppercase',
                    transition: 'background-color .12s ease',
                    mb: '3px',
                    userSelect: 'none',
                    '&:hover': {
                      bgcolor: edosActive ? 'rgba(46,177,255,.16)' : 'rgba(255,255,255,.07)',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      flexShrink: 0,
                      borderRadius: '9px',
                      display: 'grid',
                      placeItems: 'center',
                      color: '#F5A300',
                      bgcolor: 'rgba(255,255,255,.06)',
                      filter: 'brightness(1.18) saturate(1.15)',
                    }}
                  >
                    <BiIcon name="collection-fill" size={19} />
                  </Box>
                  <Box sx={{ flex: 1, lineHeight: 1.2 }}>eDOS</Box>
                  <Box
                    sx={{
                      color: 'rgba(255,255,255,0.45)',
                      transform: edosOpen ? 'rotate(180deg)' : 'rotate(0)',
                      transition: 'transform 0.18s',
                    }}
                  >
                    <BiIcon name="chevron-down" size={12} />
                  </Box>
                </Box>

                {edosOpen && (
                  <Box sx={{ mb: 0.5 }}>
                    <Typography
                      sx={{
                        fontFamily: "'MedicalPortalMono', ui-monospace, monospace",
                        fontSize: 10,
                        fontWeight: 600,
                        letterSpacing: '0.12em',
                        textTransform: 'uppercase',
                        color: '#8FB0A5',
                        px: 1.25,
                        mt: 0.25,
                        mb: 0.5,
                      }}
                    >
                      Search By
                    </Typography>
                    {EDOS_GROUP.map((sub) => {
                      const subActive = active === sub.id;
                      return (
                        <Box
                          key={sub.id}
                          onClick={() => go(sub.id)}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            position: 'relative',
                            gap: 1.25,
                            // Indented so the hierarchy reads visually.
                            pl: 2.5,
                            pr: 1.25,
                            py: 0.5,
                            minHeight: 38,
                            borderRadius: '10px',
                            border: `1px solid ${subActive ? 'rgba(46,177,255,.35)' : 'transparent'}`,
                            cursor: 'pointer',
                            color: subActive ? '#fff' : '#E2EDE8',
                            bgcolor: subActive ? 'rgba(46,177,255,.16)' : 'transparent',
                            fontSize: 11,
                            fontWeight: 600,
                            letterSpacing: '0.9px',
                            textTransform: 'uppercase',
                            transition: 'background-color .12s ease',
                            mb: '3px',
                            userSelect: 'none',
                            '&:hover': {
                              bgcolor: subActive ? 'rgba(46,177,255,.16)' : 'rgba(255,255,255,.07)',
                            },
                            ...(subActive ? {
                              '&::before': {
                                content: '""',
                                position: 'absolute',
                                left: -12,
                                top: 6,
                                bottom: 6,
                                width: 4,
                                borderRadius: '0 4px 4px 0',
                                bgcolor: sub.tint,
                              },
                            } : null),
                          }}
                        >
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              flexShrink: 0,
                              borderRadius: '8px',
                              display: 'grid',
                              placeItems: 'center',
                              color: sub.tint,
                              bgcolor: 'rgba(255,255,255,.06)',
                              filter: 'brightness(1.18) saturate(1.15)',
                            }}
                          >
                            <BiIcon name={sub.icon} size={15} />
                          </Box>
                          <Box sx={{ flex: 1, lineHeight: 1.2 }}>{sub.label}</Box>
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </>
            );
          })()}

          {/* Remaining menu items — Patient Reports through Reports &
              Analytics. Same render closure as TOP_MENU above, just
              applied to the second half of the list. */}
          {BOTTOM_MENU.map((m) => {
            const isActive = active === m.id;
            const badge = m.id === 'queries'
              ? (activeQueriesCount > 0 ? activeQueriesCount : undefined)
              : m.badge;
            return (
              <SidebarNavItem
                key={m.id}
                {...m}
                badge={badge}
                iconColor={m.tint}
                active={isActive}
                onClick={() => go(m.id)}
              />
            );
          })}

        </Box>
      }
    >
      {body}
    </PortalLayout>

    {/* Voice of Partner — auto-pops on dashboard mount when the
        active cycle is open and this lab partner hasn't submitted
        yet. Mirrors the medical portal placement. */}
    <PartnerFeedbackModal />

    {/* Forced change-password modal — pops on portal entry until
        the user sets their own password. Field semantics:
          must_change_password === false  → not yet changed → SHOW modal
          must_change_password === true   → already changed → don't show
          must_change_password === undefined (legacy) → don't show
        Strict `=== false` keeps legacy users out of the dialog. */}
    <ChangePasswordModal
      open={user?.must_change_password === false}
      onSuccess={() => { /* updateUser() inside the modal flips the
                            flag to true → this component re-renders
                            with the strict `=== false` test failing. */ }}
    />
    </>
  );
};

export default LabDashboard;
